import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Apple, Smartphone, Globe, Sparkles, Compass, Timer, TrendingUp, Workflow, Quote, Award } from "lucide-react";
import { MaskLines, FadeUp, Eyebrow, Placeholder } from "@/components/site/Motion";
import { HeroPhones } from "@/components/site/HeroPhones";
import { Marquee } from "@/components/site/Marquee";
import { AppFeature } from "@/components/site/AppFeature";
import { CtaBand } from "@/components/site/CtaBand";
import { APPS } from "@/lib/content";
import { AppleIcon, PlayIcon } from "@/components/site/StoreButtons";

const FEATURED = [
  { icon: <PlayIcon />, label: "Google Play Store" },
  { icon: <AppleIcon />, label: "Apple App Store" },
  { icon: <Globe className="h-5 w-5 text-[#0D6EFD]" />, label: "On the Web / Google" },
  { icon: <Sparkles className="h-5 w-5 text-[#0D6EFD]" />, label: "More in development", soon: true },
];

const Hero = () => (
  <section data-testid="home-hero" className="relative overflow-hidden pt-32 sm:pt-36">
    <div className="grid-lines absolute inset-0 [mask-image:radial-gradient(ellipse_at_top_left,black_25%,transparent_70%)]" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 pb-16 sm:px-8 lg:grid-cols-12 lg:pb-24">
      <div className="lg:col-span-7">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}><Eyebrow>Independent app studio</Eyebrow></motion.div>
        <h1 className="font-display mt-6 text-[2.9rem] font-extrabold leading-[0.95] tracking-tight text-[#0B132B] sm:text-6xl lg:text-[5.4rem]">
          <MaskLines lines={["We make apps", <>for <span className="text-[#0D6EFD]">your pocket.</span></>, <span className="text-slate-400">Sometimes, the web.</span>]} delay={0.15} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.65 }} className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
          Aries App Station Inc. designs, builds and publishes its own apps for the Apple App Store and Google Play, with the occasional web app when an idea calls for it.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.8 }} className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/portfolio" data-testid="hero-view-portfolio-button" className="group inline-flex items-center gap-2 rounded-full bg-[#0D6EFD] px-7 py-3.5 text-sm font-bold text-white shadow-[0_14px_30px_-12px_rgba(13,110,253,0.7)] transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-[#0B5ED7]">
            View Portfolio <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
          <Link to="/contact" data-testid="hero-contact-button" className="inline-flex items-center rounded-full border border-slate-300 px-7 py-3.5 text-sm font-bold text-[#0B132B] transition-[border-color,background-color] duration-300 hover:border-[#0B132B] hover:bg-white">
            Contact us
          </Link>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 1 }} className="mt-10">
          <Placeholder testId="hero-rating-placeholder">Placeholder: store rating badge</Placeholder>
        </motion.div>
      </div>
      <div className="lg:col-span-5"><HeroPhones /></div>
    </div>
  </section>
);

const LogoStrip = () => (
  <section data-testid="featured-in-placeholder" className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
    <p className="font-mono text-center text-xs uppercase tracking-[0.22em] text-slate-500">As featured in</p>
    <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
      {FEATURED.map((f, i) => (
        <FadeUp key={f.label} delay={i * 0.06}>
          <div data-testid={`featured-item-${i}`} className={`flex h-16 items-center justify-center gap-3 rounded-xl px-3 text-center text-sm font-semibold text-[#0B132B] transition-transform duration-300 hover:-translate-y-0.5 ${f.soon ? "border border-dashed border-slate-300" : "border border-slate-200 bg-white"}`}>
            <span className="shrink-0">{f.icon}</span>{f.label}
          </div>
        </FadeUp>
      ))}
    </div>
  </section>
);

const STATS = [
  { value: "25K+", label: "Downloads", note: "Sample figure" },
  { value: "4.8", label: "Average rating", note: "Sample figure" },
  { value: "2", label: "Apps published", note: "Trailer API & Trivia Pop" },
  { value: "3", label: "Regions reached", note: "Canada · USA · Europe" },
];

const Intro = () => (
  <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
    <div className="grid gap-12 lg:grid-cols-12">
      <FadeUp className="lg:col-span-7">
        <Eyebrow>Who we are</Eyebrow>
        <h2 className="font-display mt-5 text-3xl font-extrabold leading-[1.05] tracking-tight text-[#0B132B] sm:text-4xl lg:text-5xl">
          We build our own products, from first sketch to store listing.
        </h2>
      </FadeUp>
      <FadeUp delay={0.1} className="text-base leading-relaxed text-slate-600 md:text-lg lg:col-span-5 lg:pt-12">
        Every app on this site is ours. We decide what to build, design it, write the code, ship it to the stores and keep improving it after launch. More apps are already in the works.
      </FadeUp>
    </div>
    <div data-testid="stats-section" className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 lg:grid-cols-4">
      {STATS.map((s, i) => (
        <FadeUp key={s.label} delay={i * 0.06} className="bg-white p-7 sm:p-9">
          <p className="font-display text-5xl font-extrabold tracking-tight text-[#0B132B] sm:text-6xl">{s.value}</p>
          <p className="mt-3 text-sm font-medium text-slate-700">{s.label}</p>
          <p className="mt-1 text-xs text-slate-500">{s.note}</p>
        </FadeUp>
      ))}
    </div>
  </section>
);

const BENTO = [
  { icon: Apple, title: "iPhone & iPad apps", tag: "Primary focus", text: "Native-feeling apps published on the Apple App Store, built to feel at home on iOS.", span: "lg:col-span-7", dark: true },
  { icon: Smartphone, title: "Android apps", tag: "Primary focus", text: "The same care, shipped to Google Play for phones of every size.", span: "lg:col-span-5" },
  { icon: Globe, title: "Web apps", tag: "Occasionally", text: "When an idea works best in a browser, we build it for the web too.", span: "lg:col-span-5" },
  { icon: Sparkles, title: "Built in-house", tag: "Always", text: "Ideas, design, code, launch and updates all happen inside Aries App Station.", span: "lg:col-span-7", blue: true },
];

const BentoCard = ({ b, i }) => {
  const Icon = b.icon;
  const tone = b.dark ? "bg-[#0A0F1D] text-white" : b.blue ? "bg-[#0D6EFD] text-white" : "bg-white text-[#0B132B] border border-slate-200";
  return (
    <FadeUp delay={i * 0.07} className={`${b.span} h-full`}>
      <div data-testid={`bento-card-${i}`} className={`group relative flex h-full min-h-[260px] flex-col justify-between overflow-hidden rounded-[1.75rem] p-8 transition-transform duration-500 hover:-translate-y-1.5 sm:p-10 ${tone}`}>
        <div className="flex items-start justify-between">
          <span className={`grid h-12 w-12 place-items-center rounded-2xl ${b.dark || b.blue ? "bg-white/10" : "bg-[#0D6EFD]/10 text-[#0D6EFD]"}`}><Icon className="h-6 w-6" /></span>
          <span className={`font-mono rounded-full px-3 py-1 text-[11px] uppercase tracking-wider ${b.dark || b.blue ? "bg-white/10 text-white" : "bg-slate-100 text-slate-700"}`}>{b.tag}</span>
        </div>
        <div className="mt-10">
          <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{b.title}</h3>
          <p className={`mt-3 max-w-md text-sm leading-relaxed sm:text-base ${b.dark || b.blue ? "text-slate-200" : "text-slate-600"}`}>{b.text}</p>
        </div>
      </div>
    </FadeUp>
  );
};

const WhatWeBuild = () => (
  <section data-testid="what-we-build" className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 sm:pb-32">
    <FadeUp className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
      <div>
        <Eyebrow>What we build</Eyebrow>
        <h2 className="font-display mt-5 max-w-xl text-3xl font-extrabold tracking-tight text-[#0B132B] sm:text-4xl lg:text-5xl">Apps for the stores you already use.</h2>
      </div>
      <Link to="/about" data-testid="what-we-build-about-link" className="group inline-flex items-center gap-2 text-sm font-bold text-[#0D6EFD]">More about us <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" /></Link>
    </FadeUp>
    <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-12">
      {BENTO.map((b, i) => <BentoCard key={b.title} b={b} i={i} />)}
    </div>
  </section>
);

const FeaturedApps = () => (
  <section data-testid="featured-apps" className="grain relative bg-[#0A0F1D] py-24 sm:py-32">
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <FadeUp>
        <Eyebrow dark>Our apps</Eyebrow>
        <h2 className="font-display mt-5 max-w-2xl text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">Made by us. Shipped by us.</h2>
      </FadeUp>
      <div className="mt-16 space-y-24">
        {APPS.map((a, i) => <AppFeature key={a.id} app={a} reverse={i % 2 === 1} dark />)}
      </div>
      <FadeUp className="mt-20 flex flex-col items-start justify-between gap-6 rounded-3xl border border-white/10 p-8 sm:flex-row sm:items-center">
        <p className="font-display text-2xl font-bold text-white">More apps are in the works.</p>
        <Link to="/portfolio" data-testid="featured-apps-portfolio-link" className="group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#0B132B] transition-transform hover:-translate-y-0.5">See the portfolio <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" /></Link>
      </FadeUp>
    </div>
  </section>
);

const HOW = [
  { icon: Compass, title: "Think, then build", text: "Every app starts with a clear problem worth solving and a plan before any code is written." },
  { icon: Timer, title: "Ship and iterate", text: "We release early, listen to reviews and keep shipping updates." },
  { icon: TrendingUp, title: "Built to grow", text: "Apps are structured to scale as more people download them." },
  { icon: Workflow, title: "One studio, end to end", text: "Design, development, store listings and support all stay in-house." },
];

const How = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
    <FadeUp>
      <Eyebrow>How we work</Eyebrow>
      <h2 className="font-display mt-5 max-w-2xl text-3xl font-extrabold tracking-tight text-[#0B132B] sm:text-4xl lg:text-5xl">A small studio with a simple process.</h2>
    </FadeUp>
    <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
      {HOW.map((h, i) => {
        const Icon = h.icon;
        return (
          <FadeUp key={h.title} delay={i * 0.07} className="group bg-white p-8 transition-colors duration-300 hover:bg-[#F3F7FF]">
            <span className="font-mono text-xs text-slate-500">0{i + 1}</span>
            <Icon className="mt-6 h-7 w-7 text-[#0D6EFD] transition-transform duration-500 group-hover:-translate-y-1" />
            <h3 className="font-display mt-5 text-xl font-bold text-[#0B132B]">{h.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{h.text}</p>
          </FadeUp>
        );
      })}
    </div>
  </section>
);

const Reviews = () => (
  <section data-testid="reviews-placeholder" className="bg-[#F1F5F9] py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <FadeUp>
        <Eyebrow>What users say</Eyebrow>
        <h2 className="font-display mt-5 text-3xl font-extrabold tracking-tight text-[#0B132B] sm:text-4xl lg:text-5xl">Reviews from the stores.</h2>
      </FadeUp>
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <FadeUp key={i} delay={i * 0.08} className="rounded-3xl border border-dashed border-slate-300 bg-white p-8">
            <Quote className="h-7 w-7 text-[#0D6EFD]" />
            <p className="mt-5 text-base leading-relaxed text-slate-600">[Placeholder] Paste a real App Store or Google Play review here.</p>
            <p className="mt-6 text-sm font-semibold text-[#0B132B]">[Reviewer name]</p>
            <p className="text-xs text-slate-500">[App name · Store]</p>
          </FadeUp>
        ))}
      </div>
      <div data-testid="awards-placeholder" className="mt-16 grid grid-cols-2 gap-4 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <FadeUp key={i} delay={i * 0.05} className="flex items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white p-5">
            <Award className="h-6 w-6 shrink-0 text-[#0D6EFD]" />
            <span className="text-xs font-medium text-slate-600">Award or recognition placeholder</span>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <LogoStrip />
      <Intro />
      <WhatWeBuild />
      <FeaturedApps />
      <How />
      <Reviews />
      <CtaBand />
    </div>
  );
}
